import * as THREE from 'three';
import { InstancedLayer } from './instancing';
import { createLanternLights } from './lantern';
import { isGenerated, PACKS_URL } from './library';
import type { Layout } from './types';

// Production side of placement: read a committed layout and draw it.
// No editor, no raycasts, no manifests — the y values were baked at authoring
// time, so this is purely "load GLBs, fill instance buffers".

export interface PlacementRuntime {
    readonly group: THREE.Group;
    /** Drive the lantern light pool. Safe to call every frame; no-op without lanterns. */
    update(camera: THREE.Camera): void;
    /** 0 = day, 1 = night. Lanterns only burn after dark. */
    setNight(night: number): void;
    dispose(): void;
}

export async function loadLayout(opts: {
    scene: THREE.Scene;
    /** URL of a committed layout JSON (typically /layouts/<site>.json). */
    url: string;
    unitsPerMeter: number;
    /** Host's main model; receives the layout's alignment offset if present. */
    model?: THREE.Object3D;
}): Promise<PlacementRuntime | null> {
    const { scene, url, unitsPerMeter, model } = opts;

    let layout: Layout;
    try {
        const res = await fetch(url);
        // A missing layout is the normal state for a project that has not
        // authored one yet — silently no-op, never an error banner.
        if (!res.ok) return null;
        layout = (await res.json()) as Layout;
    } catch {
        return null;
    }
    if (layout.version !== 1 || !Array.isArray(layout.layers)) {
        console.warn('[placement] unrecognised layout format at', url);
        return null;
    }

    if (model && layout.modelOffset) {
        const [x, y, z] = layout.modelOffset;
        model.position.x += x * unitsPerMeter;
        model.position.y += y * unitsPerMeter;
        model.position.z += z * unitsPerMeter;
    }
    if (model && layout.modelRotationY) {
        model.rotation.y += (layout.modelRotationY * Math.PI) / 180;
    }

    const group = new THREE.Group();
    group.name = 'placement';
    scene.add(group);

    const layers: InstancedLayer[] = [];
    const lanternPositions: THREE.Vector3[] = [];
    await Promise.all(
        layout.layers.map(async ({ asset, items }) => {
            if (!items.length) return;
            try {
                const source = isGenerated(asset) ? asset : `${PACKS_URL}/${asset}.glb`;
                const layer = await InstancedLayer.create(source, unitsPerMeter);
                layer.setItems(items);
                group.add(layer.group);
                layers.push(layer);
                if (asset === 'generated/lantern') {
                    lanternPositions.push(...layer.instanceWorldPositions(items));
                }
            } catch (error) {
                console.warn(`[placement] layer "${asset}" failed to load:`, error);
            }
        }),
    );
    const total = layout.layers.reduce((n, l) => n + l.items.length, 0);
    console.info(`[placement] layout "${layout.site}": ${total} items in ${layers.length} layers`);

    // Real light only where there are lanterns to justify it.
    const lanterns = lanternPositions.length
        ? createLanternLights({ scene, unitsPerMeter })
        : null;
    lanterns?.setPositions(lanternPositions);
    if (lanternPositions.length) {
        console.info(`[placement] ${lanternPositions.length} lanterns lit`);
    }

    return {
        group,
        update(camera) {
            lanterns?.update(camera);
        },
        setNight(night) {
            lanterns?.setNight(night);
        },
        dispose() {
            for (const layer of layers) layer.dispose();
            lanterns?.dispose();
            scene.remove(group);
        },
    };
}
